/**
 * Owner: Masa Bayu
 * File: LandingHome.tsx
 * Date:Time: 2026-10-18 16:35
 * Description: Home landing §376 — orkestrator utama: stor/state/effect
 *   dikumpul di sini, zon-zon dibina daripada fail pecahan (≤500 baris per fail).
 *   Replika 100% mockup — satu DOM, tiga band melalui CSS; semua data
 *   daripada projeksi katalog/library sebenar. §468i — rak koleksi di atas
 *   Featured Books; §468n — tinggal Masa Bayu · Alamtologi.
 */

import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { getDemoCart } from '../../../commerce/demo-cart-store';
import { getCatalogVersion, subscribeCatalog } from '../../../publication/catalog-port';
import { addDemoWishlistTitle, removeDemoWishlistTitle } from '../../../library/demo-library-store';
import { useHubSession } from '../../../auth/use-hub-session';
import { HERO_BANNER_SLIDES } from '../../../ux/hero-banners';
import {
  MARKETPLACE_FILTERS_EMPTY,
  categoryLabelsFor,
  listMarketplaceBooks,
  matchesFilters,
  type MarketplaceCategoryId,
} from '../marketplace-projection';
import { getMarketplaceLocaleProfile, resolveMarketplaceLabels } from '../marketplace-locale';
import {
  bookCollections,
  continueReadingEntries,
  featuredBooks,
  popularCategories,
  topAuthors,
  trendingBooks,
  wishlistedIds,
} from './landing-data';
import { resolveLandingLabels } from './landing-locale';
import {
  listLandingAlerts,
  markLandingAlertsRead,
  subscribeLandingAlerts,
  unreadLandingAlertCount,
} from './landing-alerts';
import { LandingMobileChrome, LandingTopBar } from './LandingHeader';
import { LandingInstallBanner } from './LandingInstallBanner';
import { LandingHero } from './LandingHero';
import { LandingFooter, LandingRail, LandingSidebar, LandingTabBar } from './LandingNav';
import {
  BookShelfSection,
  CategoryStrip,
  CollectionShelves,
  ContinueReading,
  ExploreBanner,
} from './LandingSections';
import { AUTO_MS } from './landing-shared';
import './landing.css';
import './landing-header.css';
import './landing-hero.css';
import './landing-cards.css';
import './landing-strip.css';
import './landing-nav.css';
import './landing-rail.css';
import './landing-bands.css';

export interface LandingHomeProps {
  readonly onNavigate: (path: string) => void;
}

export function LandingHome({ onNavigate }: LandingHomeProps): ReactElement {
  const catalogVersion = useSyncExternalStore(subscribeCatalog, getCatalogVersion);
  const books = useMemo(() => listMarketplaceBooks(), [catalogVersion]);
  const profile = useMemo(() => getMarketplaceLocaleProfile(), []);
  const t = useMemo(() => resolveLandingLabels(profile), [profile]);
  const tc = useMemo(() => resolveMarketplaceLabels(profile), [profile]);
  const categoryLabels = useMemo(() => categoryLabelsFor(tc.language), [tc]);
  const session = useHubSession();

  // §409 — ruling Owner: pilihan kategori strip menapis rak buku home di tempat.
  const [filterCat, setFilterCat] = useState<MarketplaceCategoryId | 'all'>('all');
  const shelfBooks = useMemo(
    () =>
      filterCat === 'all'
        ? books
        : books.filter((book) =>
            matchesFilters(book, { ...MARKETPLACE_FILTERS_EMPTY, category: filterCat }),
          ),
    [books, filterCat],
  );

  const featured = useMemo(() => featuredBooks(shelfBooks), [shelfBooks]);
  const trending = useMemo(() => trendingBooks(shelfBooks), [shelfBooks]);
  // §468i/§468n — rak koleksi (Masa Bayu · Alamtologi).
  const collections = useMemo(() => bookCollections(shelfBooks), [shelfBooks]);
  const authors = useMemo(() => topAuthors(books), [books]);
  const categoryCounts = useMemo(() => popularCategories(books), [books]);
  const continueEntries = useMemo(() => continueReadingEntries(), [catalogVersion]);

  const [cartCount] = useState(() => getDemoCart().lines.length);
  const [wish, setWish] = useState<ReadonlySet<string>>(() => new Set(wishlistedIds()));
  const [query, setQuery] = useState('');
  const [heroIndex, setHeroIndex] = useState(0);
  const [heroPaused, setHeroPaused] = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [alertsTick, setAlertsTick] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);
  const alertsRef = useRef<HTMLDivElement>(null);
  // §462 — sauh sheet loceng bar bawah (berasingan daripada loceng topbar).
  const barAlertsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (heroPaused || HERO_BANNER_SLIDES.length < 2) {
      return;
    }
    if (typeof window.matchMedia === 'function') {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (reduce.matches) {
        return;
      }
    }
    const id = window.setInterval(() => {
      setHeroIndex((current) => (current + 1) % HERO_BANNER_SLIDES.length);
    }, AUTO_MS);
    return () => {
      window.clearInterval(id);
    };
  }, [heroPaused]);

  useEffect(() => subscribeLandingAlerts(() => setAlertsTick((tick) => tick + 1)), []);

  useEffect(() => {
    if (!alertsOpen && !filterOpen) {
      return;
    }
    const onDocDown = (event: MouseEvent): void => {
      const target = event.target as Node;
      if (alertsOpen) {
        const inHeader = alertsRef.current?.contains(target) ?? false;
        const inBar = barAlertsRef.current?.contains(target) ?? false;
        if (!inHeader && !inBar) {
          setAlertsOpen(false);
        }
      }
      if (filterOpen) {
        const sheet = document.querySelector('.qxl__filter-sheet');
        const trigger = document.querySelector('.qxl__filter-btn');
        if (
          sheet instanceof HTMLElement &&
          !(sheet.contains(target) || trigger?.contains(target))
        ) {
          setFilterOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [alertsOpen, filterOpen]);

  const alerts = useMemo(() => listLandingAlerts(), [alertsOpen, alertsTick]);
  const unread = useMemo(() => unreadLandingAlertCount(), [alertsOpen, alertsTick]);

  const submitSearch = (event: FormEvent): void => {
    event.preventDefault();
    const q = query.trim();
    onNavigate(q.length > 0 ? `/all-books?q=${encodeURIComponent(q)}` : '/all-books');
  };

  const toggleWish = (publicationId: string): void => {
    if (wish.has(publicationId)) {
      removeDemoWishlistTitle(publicationId);
    } else {
      addDemoWishlistTitle(publicationId);
    }
    setWish(new Set(wishlistedIds()));
  };

  const openAlerts = (): void => {
    if (!alertsOpen) {
      markLandingAlertsRead();
    }
    setAlertsOpen(!alertsOpen);
  };

  const applyFilter = (): void => {
    // §409 — sheet hanya memilih kategori; rak home sudah menapis di tempat.
    setFilterOpen(false);
  };

  const headerProps = {
    t,
    categoryLabels,
    query,
    onQuery: setQuery,
    onSubmitSearch: submitSearch,
    filterCat,
    onFilterCat: setFilterCat,
    unread,
    alertsOpen,
    alerts: alerts.map((alert) => ({ id: alert.id, text: alert.text })),
    onBellToggle: openAlerts,
    alertsRef,
    onNavigate,
    signedIn: session.signedIn,
  };

  return (
    <div className="qxl" data-qx-landing="true">
      <LandingMobileChrome
        {...headerProps}
        filterOpen={filterOpen}
        onFilterToggle={() => {
          setFilterOpen(!filterOpen);
        }}
        onFilterApply={applyFilter}
      />
      <LandingTopBar
        {...headerProps}
        cartCount={cartCount}
        displayName={session.signedIn && session.signedInName ? session.signedInName : t.signIn}
      />

      <div className="qxl__frame">
        <LandingSidebar t={t} onNavigate={onNavigate} />
        <div className="qxl__main">
          <LandingHero
            t={t}
            index={heroIndex}
            onIndex={setHeroIndex}
            onHover={setHeroPaused}
            onBrowse={() => {
              onNavigate('/all-books');
            }}
            query={query}
            onQuery={setQuery}
            onSearchGo={() => {
              const q = query.trim();
              onNavigate(q.length > 0 ? `/all-books?q=${encodeURIComponent(q)}` : '/all-books');
            }}
          />

          <CategoryStrip
            t={t}
            tc={categoryLabels}
            onNavigate={onNavigate}
            filterCat={filterCat}
            onFilterCat={setFilterCat}
          />

          <CollectionShelves
            collections={collections}
            t={t}
            currency={profile.currency}
            categoryLabels={categoryLabels}
            wish={wish}
            onWish={toggleWish}
            onNavigate={onNavigate}
          />

          <BookShelfSection
            id="qxl-featured"
            icon="star"
            title={t.sections.featured}
            seeAll={t.sections.seeAll}
            onSeeAll={() => {
              onNavigate(filterCat === 'all' ? '/all-books' : `/all-books?category=${filterCat}`);
            }}
            gridClassName="qxl__grid qxl__grid--featured"
            gridAriaLabel={t.card.featuredGridAria}
            books={featured}
            t={t}
            currency={profile.currency}
            categoryLabels={categoryLabels}
            wish={wish}
            onWish={toggleWish}
            onNavigate={onNavigate}
            emptyText={t.sections.emptyShelf}
          />

          <BookShelfSection
            id="qxl-trending"
            icon="flame"
            title={t.sections.trending}
            seeAll={t.sections.seeAll}
            onSeeAll={() => {
              onNavigate(
                filterCat === 'all'
                  ? '/all-books?sort=popular'
                  : `/all-books?category=${filterCat}&sort=popular`,
              );
            }}
            gridClassName="qxl__grid qxl__grid--trending"
            gridAriaLabel={t.card.trendingGridAria}
            books={trending}
            t={t}
            currency={profile.currency}
            categoryLabels={categoryLabels}
            wish={wish}
            onWish={toggleWish}
            onNavigate={onNavigate}
            variant="trending"
            emptyText={t.sections.emptyShelf}
          />

          {continueEntries.length > 0 ? (
            <ContinueReading t={t} entries={continueEntries} onNavigate={onNavigate} />
          ) : null}

          <ExploreBanner t={t} onNavigate={onNavigate} />
        </div>
        <LandingRail
          t={t}
          tc={categoryLabels}
          authors={authors}
          categoryCounts={categoryCounts}
          onNavigate={onNavigate}
        />
      </div>

      <LandingFooter t={t} />
      <LandingTabBar
        t={t}
        onNavigate={onNavigate}
        unread={unread}
        alertsOpen={alertsOpen}
        alerts={alerts.map((alert) => ({ id: alert.id, text: alert.text }))}
        onBellToggle={openAlerts}
        alertsRef={barAlertsRef}
      />
      <LandingInstallBanner t={t} />
    </div>
  );
}
